/*
📦 react-markdown-registry
Drop-in enhanced replacement for `react-markdown`, with:
- LLM-safe component registry
- Smart prop merging via @@json@@ annotation  
- Security by design - only registered components render
- Support for streaming + memoization
- Full compatibility with remark plugins
*/

import { useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import ReactMarkdown, { Options as ReactMarkdownProps } from "react-markdown";
import { Pluggable } from "unified";
import { DeferredRender } from "./components";
import { parseRegistryElements } from "./parseRegistryElements";
import { ComponentRegistry, mergeMarkdownProps } from "./utils";

// ---- Main Component ----
export interface ExtendedMarkdownProps<Registry extends ComponentRegistry>
  extends ReactMarkdownProps {
  registry: Registry;
  children: string;
  mode?: string;
  enableDeferredRender?: boolean;
}

export function ExtendedMarkdown<Registry extends ComponentRegistry>({
  children,
  registry,
  mode,
  enableDeferredRender = true,
  components: userComponents = {},
  remarkPlugins = [],
  ...rest
}: ExtendedMarkdownProps<Registry>) {
  const mergedComponents = useMemo(() => {
    const registryComponents = Object.fromEntries(
      Object.entries(registry).map(
        ([
          tag,
          {
            defaultComponent,
            modes,
            additionalPropValues,
            errorFallback: ErrorFallback,
            placeholder: PlaceholderFallback,
          },
        ]) => {
          return [
            tag,
            (markdownProps: any) => {
              // Determine which component to use based on mode
              let Component: React.ComponentType<any> | null = null;

              if (mode) {
                // Mode is set - use mode-specific component if it exists, otherwise null
                Component = modes?.[mode] || null;
              } else {
                // No mode set - use default component
                Component = defaultComponent;
              }

              // If no component to render, return null
              if (!Component) {
                return null;
              }

              const merged = mergeMarkdownProps(
                markdownProps,
                additionalPropValues
              );

              const componentElement = <Component {...merged} />;

              const renderContent = enableDeferredRender ? (
                <DeferredRender
                  fallback={
                    PlaceholderFallback ? <PlaceholderFallback /> : null
                  }
                >
                  {componentElement}
                </DeferredRender>
              ) : (
                componentElement
              );

              return (
                <ErrorBoundary
                  fallback={ErrorFallback ? <ErrorFallback /> : null}
                >
                  {renderContent}
                </ErrorBoundary>
              );
            },
          ];
        }
      )
    );

    // Add placeholder component handler
    const placeholderComponent = ({ tagName }: { tagName: string }) => {
      const registryEntry = registry[tagName];
      if (registryEntry?.placeholder) {
        const PlaceholderComponent = registryEntry.placeholder;
        return <PlaceholderComponent />;
      }
      return null; // No placeholder defined, render nothing
    };

    return {
      ...userComponents,
      ...registryComponents,
      placeholder: placeholderComponent,
    };
  }, [registry, userComponents, mode, enableDeferredRender]);

  const fullRemarkPlugins = useMemo((): Pluggable[] => {
    const safeUserPlugins = Array.isArray(remarkPlugins) ? remarkPlugins : [];

    return [
      [parseRegistryElements, { registry }], // Convert registry component tags to custom MDAST nodes
      ...safeUserPlugins,
    ];
  }, [remarkPlugins, registry]);

  return (
    <ReactMarkdown
      {...rest}
      remarkPlugins={fullRemarkPlugins}
      components={mergedComponents}
    >
      {children}
    </ReactMarkdown>
  );
}

// ---- Exports ----
export default ExtendedMarkdown;
